/**
 * ModalInMobile gives possibility separate part of existing DOM so that in mobile views that
 * fragment is shown in a separate modal layer on top of the page.
 *
 * Currently, this does not implement resize listener for window.
 *
 * Example:
 * <Parent>
 *   <ModalInMobile isModalOpenOnMobile={modalOpen} onClose={handleClose}>
 *     <FormX />
 *   </ModalInMobile>
 * </Parent>
 */
import React, { useEffect, useState } from 'react';

import { withViewport } from '../../../util/contextHelpers';
import Modal from '../Modal';

/**
 * Same boundary as useScreenSize and isMobileSize: the modal layout ends at maxWidth itself.
 *
 * @param {number} width
 * @param {number} maxWidth
 * @returns {boolean}
 */
export const isModalLayout = (width, maxWidth) => width < maxWidth;

// .root — ModalInMobile is visible by default
const rootSx = { width: '100%' };

// .modalHidden — content hidden in mobile layout
const modalHiddenSx = { display: 'none' };

// .modalContainer (--marketplaceModalInMobileBaseStyles + md flex-basis 576px)
const modalContainerSx = (theme) => ({
  ...theme.mixins.marketplaceModalInMobileBaseStyles,
  // Merge the mixin's md block with the own md override (same media key);
  // flex-basis 576px overrides the mixin's 480px.
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceModalInMobileBaseStyles['@media (min-width:900px)'],
    flexBasis: '576px',
  },
});

// .modalInMobile
const modalInMobileSx = { height: 'calc(100% - var(--BottomBar_height))' };

// .modalContent
const modalContentSx = (theme) => ({
  width: '100%',
  height: '100%',
  [theme.breakpoints.up('md')]: {
    height: '100%',
    // ModalInMobile content should work with box-shadows etc.
    overflow: 'visible',
  },
});

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.className]
 * @param {string|null} [props.containerClassName]
 * @param {string} props.id
 * @param {number} [props.showAsModalMaxWidth]
 * @param {string|null} [props.closeButtonMessage]
 * @param {Function} [props.onClose]
 * @param {Function} props.onManageDisableScrolling
 * @param {Object} props.viewport
 * @param {React.ReactNode|null} [props.changeCloseButton]
 * @param {boolean} props.isModalOpenOnMobile
 */
const ModalInMobileComponent = ({
  children = null,
  className = '',
  containerClassName = null,
  id,
  showAsModalMaxWidth = 0,
  closeButtonMessage = null,
  onClose = null,
  onManageDisableScrolling,
  viewport,
  changeCloseButton = null,
  isModalOpenOnMobile,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isMobileLayout = isModalLayout(viewport.width, showAsModalMaxWidth);

  useEffect(() => {
    // Sync open status whenever it is changing on mobile layout or it is
    // closing (on desktop layout). This covers both the initial mount
    // adaptation to screen size and subsequent prop/layout changes.
    const isChanging = isModalOpenOnMobile !== isOpen;
    const shouldBeClosedAsModal = !isMobileLayout && !isModalOpenOnMobile;

    if (isChanging && (isMobileLayout || shouldBeClosedAsModal)) {
      setIsOpen(isModalOpenOnMobile);
    }
  }, [isModalOpenOnMobile, isOpen, isMobileLayout]);

  const handleClose = (event) => {
    setIsOpen(false);
    if (onClose) {
      onClose(event);
    }
  };

  const isOpenInMobile = isOpen;
  const isClosedInMobile = isMobileLayout && !isOpenInMobile;
  const isOpenAsModal = isOpenInMobile && isMobileLayout;

  // Container: a caller-supplied containerClassName (a class string) overrides the default
  // .modalContainer styles (so we only apply modalContainerSx when no containerClassName is given);
  // the .modalInMobile height is added when open. Passed to Modal as containerSx (replace-semantics).
  const containerSx = (theme) => ({
    ...(containerClassName ? {} : modalContainerSx(theme)),
    ...(isOpenInMobile ? modalInMobileSx : {}),
  });

  return (
    <Modal
      changeCloseButton={changeCloseButton}
      className={className || undefined}
      closeButtonMessage={closeButtonMessage}
      containerClassName={containerClassName || undefined}
      containerSx={containerSx}
      contentSx={modalContentSx}
      id={id}
      isClosedClassName={null}
      isOpen={isOpenAsModal}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      sx={isClosedInMobile ? { ...rootSx, ...modalHiddenSx } : rootSx}
    >
      {children}
    </Modal>
  );
};

const ModalInMobile = withViewport(ModalInMobileComponent);

export default ModalInMobile;
